import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';
import { auth } from '../../firebase-config';
import { useForm } from '../../hooks/useForm';
import Logo from '../../components/logo/logo';
import Button from '../../components/button/button';
import InputField from '../../components/input-field/input-field';
import TagManager from 'react-gtm-module';
import { attributionFromSearch } from '../../modules/signup-tracking-data.js';
import { rememberSignupAttribution, persistSignupAttribution } from '../../modules/signup-tracking.js';

const validateSignupForm = (values) => {
  let errors = {};

  if (!values.registerEmail.trim()) errors.registerEmail = 'Email mangler';
  if (!values.registerPassword.trim())
    errors.registerPassword = 'Password mangler';
  if (!values.registerRepeatPassword.trim())
    errors.registerRepeatPassword = 'Gentag password';
  if (!values.acceptTerms) errors.acceptTerms = 'Skal accepteres';

  return errors;
};

export default function SignupView({ setIsRegistering }) {
  const navigate = useNavigate();
  const [repeatPasswordErrorMessage, setRepeatPasswordErrorMessage] =
    useState('');

  const [attribution] = useState(() => attributionFromSearch(window.location.search));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupError, setSignupError] = useState('');
  const submitting = useRef(false);
  const createdUser = useRef(null);

  // Function to handle user registration
  const onSubmit = useCallback(
    async (values) => {
      const { registerEmail, registerPassword, registerRepeatPassword } =
        values;

      // Confirm that the passwords match
      if (registerPassword !== registerRepeatPassword) {
        setRepeatPasswordErrorMessage('Passwords skal være ens');
        return;
      }

      if (submitting.current) return;
      submitting.current = true;
      setIsSubmitting(true);
      setSignupError('');
      // Keep this form visible until attribution is saved and the email is sent.
      setIsRegistering(true);

      try {
        if (!createdUser.current) {
          const credential = await createUserWithEmailAndPassword(auth, registerEmail, registerPassword);
          createdUser.current = credential.user;
          rememberSignupAttribution(credential.user.uid, attribution);
        }

        await persistSignupAttribution(createdUser.current);

        // The email is sent only after Firebase has retained the campaign data.
        await sendEmailVerification(createdUser.current);

        // Trigger data layer event for user registration
        TagManager.dataLayer({
          dataLayer: {
            event: 'Opret bruger',
            label: 'Udfyldt formular og oprettet bruger',
          },
        });

        // Redirect the user to the verify email view
        navigate('/verify-email');
        setIsRegistering(false);
      } catch (error) {
        console.error('Error preparing signup:', error);
        setSignupError('Oprettelsen kunne ikke færdiggøres. Prøv igen.');
        if (!createdUser.current) setIsRegistering(false);
      } finally {
        submitting.current = false;
        setIsSubmitting(false);
      }
    },
    [navigate, attribution, setIsRegistering]
  );

  const { values, handleChange, handleSubmit, errors } = useForm(
    {
      registerEmail: '',
      registerPassword: '',
      registerRepeatPassword: '',
      acceptTerms: false,
    },
    validateSignupForm,
    onSubmit
  );

  return (
    <div className="centered-layout">
      <Logo />
      <div className="centered-layout__content">
        <form className="centered-layout__form" onSubmit={handleSubmit}>
          <h3>Opret ny bruger</h3>
          <InputField
            name="registerEmail"
            disabled={isSubmitting || !!createdUser.current}
            label="Email"
            required
            type="email"
            placeholder="Skriv din email"
            onChange={handleChange}
            value={values.registerEmail}
            error={errors.registerEmail}
          />

          <InputField
            name="registerPassword"
            disabled={isSubmitting || !!createdUser.current}
            label="Kodeord"
            required
            type="password"
            placeholder="Skriv dit kodeord"
            onChange={handleChange}
            value={values.registerPassword}
            error={errors.registerPassword}
          />

          <InputField
            name="registerRepeatPassword"
            disabled={isSubmitting || !!createdUser.current}
            label="Gentag kodeord"
            required
            type="password"
            placeholder="Gentag dit kodeord"
            onChange={handleChange}
            value={values.registerRepeatPassword}
            error={errors.registerRepeatPassword || repeatPasswordErrorMessage}
          />

          <InputField
            name="acceptTerms"
            type="checkbox"
            label={`Jeg accepterer <a href="https://www.hrfamly.dk/sikkerhed/betingelser/" target="_blank">betingelser</a> og <a href="https://www.hrfamly.dk/sikkerhed/databehandleraftale/" target="_blank">databehandleraftale</a>`}
            required
            onChange={handleChange}
            value={values.acceptTerms}
            error={errors.acceptTerms}
          />
          {signupError && <p role="alert">{signupError}</p>}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Opretter...' : 'Opret bruger'}
          </Button>
        </form>

        <div className="centered-layout__bottom">
          <h4>Har du allerede en bruger?</h4>
          <Button buttonStyle="text-link" href="/">
            Log ind
          </Button>
        </div>
      </div>
    </div>
  );
}
